import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { NewPurchaseOrderConfig, InvestmentConfig } from '../../../../models/makers-finances-mgr.models';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import {
  DETAIL_MODE, DetailMode, SourceType,
  PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION,
  INVESTMENT_FIELDS, InvestmentField,
} from '../../purchase-order.constants';
import { SelectOption, staticSource, staticSourceFromTaxOptions } from '../section-base.types';

@Component({
  selector: 'app-section-budget-dates',
  templateUrl: './section-budget-dates.component.html',
  styleUrls: ['./section-budget-dates.component.scss'],
})
export class SectionBudgetDatesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() mode: DetailMode;
  @Input() sourceType: SourceType;
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  @Input() catalogsLoaded = false;
  @Input() submitted = false;
  @Input() fieldConfigs: Record<string, any> = {};
  // Edit mode: the PO id is passed as excludeId to the external-id availability check so the PO
  // doesn't conflict with itself.
  @Input() poId: number | null = null;
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the currency/tax selects (one-select is not form-bound).
  @Input() poLoaded = false;

  // In edit mode the skeleton stays until the PO detail is also loaded (poLoaded), not just the
  // catalogs. In create mode only catalogsLoaded matters.
  get showSkeleton(): boolean {
    return !this.catalogsLoaded || (this.mode === DETAIL_MODE.EDIT && !this.poLoaded);
  }

  @ViewChild('poCurrencySelect') poCurrencySelect: SelectComponent<SelectOption>;
  @ViewChild('taxPercentageSelect') taxPercentageSelect: SelectComponent<SelectOption>;

  private readonly destroy$ = new Subject<void>();

  // Non-blocking warning message from the external-id availability check. Shown visually below the
  // input but does NOT make the form invalid — the user can still save draft or submit. At submit
  // time, the parent component re-checks and may show a confirmation modal.
  externalIdWarning: string | null = null;

  constructor(
    private readonly storeAux: StoreAuxService,
    private readonly financesService: MakersFinancesMgrService,
  ) {}

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.BUDGET_AND_DATES];
  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};

  private _investmentCfg: Record<InvestmentField, InvestmentConfig> = {
    [PO_FIELD.CO_INVESTMENT]: { max_percentage: 100 },
    [PO_FIELD.RE_INVESTMENT]: { max_percentage: 100 },
  };

  ngOnInit(): void {
    const poNumberCtrl = this.form.get(PO_FIELD.PO_NUMBER);

    poNumberCtrl?.valueChanges.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      switchMap(value => {
        this.externalIdWarning = null;
        const companyId = this.form.get(PO_FIELD.COMPANY_TO_BILL)?.value;
        if (!value || !companyId) {
          return of(null);
        }
        return this.financesService.checkExternalIdAvailability(value, companyId, this.storeAux.country, this.poId ?? undefined).pipe(
          catchError(() => of(null))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(res => {
      if (res?.data && !res.data.available) {
        this.externalIdWarning = res.data.warning
          || (res.data.conflicting_po_id != null
            ? `There is another PO (${res.data.conflicting_po_id}) using the same External ID`
            : 'External ID is already in use');
      } else {
        this.externalIdWarning = null;
      }
    });

    this.form.get(PO_FIELD.COMPANY_TO_BILL)?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.externalIdWarning = null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['remoteConfig'] && this.remoteConfig) {
      this.selectSources[PO_FIELD.PO_CURRENCY] = staticSource(this.remoteConfig.currencies);
      if (this.remoteConfig.tax_percentage_options?.length) {
        this.selectSources[PO_FIELD.TAX_PERCENTAGE] = staticSourceFromTaxOptions(this.remoteConfig.tax_percentage_options);
      }
      INVESTMENT_FIELDS.forEach(key => {
        const investmentCfg = this.remoteConfig?.[key];
        if (investmentCfg) {
          this._investmentCfg[key] = investmentCfg;
        }
      });
      this._updateTaxPercentageValidator();
    }

    if (changes['sourceType']) {
      this._updateTaxPercentageValidator();
    }

    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => this._prefillSelects());
    }
  }

  // Edit mode: sync the currency/tax select UIs to the patched form values. The form already holds
  // the values; the selects just need their display set (one-select is not form-bound).
  private _prefillSelects(): void {
    const currency = this.form.get(PO_FIELD.PO_CURRENCY)?.value;
    if (currency && this.poCurrencySelect) {
      this.poCurrencySelect.setSelection({ id: currency, name: currency });
    }
    const taxValue = this.form.get(PO_FIELD.TAX_PERCENTAGE)?.value;
    if (taxValue != null && this.taxPercentageSelect && this.remoteConfig?.tax_percentage_options?.length) {
      const match = this.remoteConfig.tax_percentage_options.find(o => String(o.value) === String(taxValue));
      if (match) {
        this.taxPercentageSelect.setSelection({ id: String(match.value), name: match.label });
      }
    }
  }

  get isInvestmentVisible(): boolean {
    return !!this.fieldConfigs[PO_FIELD.CO_INVESTMENT]?.visible_by_source?.[this.sourceType];
  }

  get isPoNumberVisible(): boolean {
    return !!this.fieldConfigs[PO_FIELD.PO_NUMBER]?.visible_by_source?.[this.sourceType];
  }

  get isTaxPercentageVisible(): boolean {
    return this.storeAux.country === 'BR'
      && !!this.fieldConfigs[PO_FIELD.TAX_PERCENTAGE]?.visible_by_source?.[this.sourceType]
      && !!(this.remoteConfig?.tax_percentage_options?.length);
  }

  getInvestmentExceeded(fieldKey: string): boolean {
    const cfg = this._investmentCfg[fieldKey as InvestmentField];
    if (!cfg) {
      return false;
    }
    const value = parseFloat(this.form?.get(fieldKey)?.value);
    return !isNaN(value) && value > cfg.max_percentage;
  }

  getInvestmentWarning(fieldKey: string): string | null {
    if (!this.getInvestmentExceeded(fieldKey)) {
      return null;
    }
    const cfg = this._investmentCfg[fieldKey as InvestmentField];
    const raw = cfg?.warning_message ?? 'The percentage exceeds the max recommended for this country';
    const maxPercentageLabel = `${cfg?.max_percentage}%`;
    return `*${raw.replace(/\{\{[^}]+\}\}/g, maxPercentageLabel)}`;
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    this.form.get(fieldKey)?.setValue(value?.id ?? null);
  }

  isInvalid(key: string): boolean {
    if (!this.submitted) {
      return false;
    }
    const ctrl = this.form.get(key);
    return !ctrl?.value || !!ctrl.errors?.['required'];
  }

  private _updateTaxPercentageValidator(): void {
    const ctrl = this.form?.get(PO_FIELD.TAX_PERCENTAGE);
    if (this.isTaxPercentageVisible) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }
}
